import { useEffect, useState } from 'react';

import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { authApi } from '@/api';
import CountryPhoneInput from '@/components/RPhoneInput';
import Redirector from '@/components/RRedirector';
import LocalStorageService from '@/services/LocalStorageService';

import StyledContactUs from './styles';

const { Option } = Select;
const ORGANIZATION_TYPE_COMPANY = 'company';
const ORGANIZATION_TYPE_ACADEMY = 'academy';

const { Success } = Redirector;

const ContactUs = () => {
  const { t } = useTranslation();
  const [organizationType, setOrganizationType] = useState<
    typeof ORGANIZATION_TYPE_COMPANY | typeof ORGANIZATION_TYPE_ACADEMY | ''
  >('');

  const [submittable, setSubmittable] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isContactSuccess, setIsContactSuccess] = useState<boolean>(false);
  const [form] = useForm();
  const navigate = useNavigate();
  const values = Form.useWatch([], form);

  const handleSubmit = async () => {
    try {
      const formData = form.getFieldsValue();
      const formattedData = {
        name: formData.name,
        surname: formData.surname,
        title: formData.title || `${formData.name} ${formData.surname}`,
        phoneNumber: `+${formData.phone.code}${formData.phone.phone}`,
        organizationType: formData.organization,
        organizationName: formData.organizationName,
        organizationEmail: formData.organizationEmail,
        message: formData.message,
      };
      LocalStorageService.clearLocalStorage();
      await authApi.postContact(formattedData);
      setIsContactSuccess(true);
    } catch (error) {
      setLoading(false);
      console.error('Error submitting form: ', error);
    }
  };

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values, organizationType]);

  return (
    <>
      {isContactSuccess ? (
        <Redirector>
          <Success description={t('register.contactUs.success.description')} />
        </Redirector>
      ) : (
        <StyledContactUs>
          <Flex vertical className="container">
            <Typography.Title level={2} className="title">
              {t('register.contactUs.title')}
            </Typography.Title>
            <Typography.Text className="description">
              {t('register.contactUs.description')}
            </Typography.Text>
            <hr className="line" />
            {loading ? (
              <Flex
                vertical
                className="w-100"
                align="center"
                justify="center"
                style={{ height: '400px' }}
              >
                <Spin />
              </Flex>
            ) : (
              <>
                <Form
                  form={form}
                  layout="vertical"
                  className="form-contact-us"
                  onFinish={handleSubmit}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="name"
                        label={t('register.contactUs.fields.name.label')}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'register.contactUs.fields.name.required'
                            ),
                          },
                          {
                            pattern: /^[A-Za-z ]+$/,
                            message: t(
                              'register.contactUs.fields.name.pattern'
                            ),
                          },
                        ]}
                      >
                        <Input
                          className="input"
                          placeholder={t(
                            'register.contactUs.fields.name.placeholder'
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="surname"
                        label={t('register.contactUs.fields.surname.label')}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'register.contactUs.fields.surname.required'
                            ),
                          },
                          {
                            pattern: /^[A-Za-z ]+$/,
                            message: t(
                              'register.contactUs.fields.surname.pattern'
                            ),
                          },
                        ]}
                      >
                        <Input
                          className="input"
                          placeholder={t(
                            'register.contactUs.fields.surname.placeholder'
                          )}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="phone"
                        label={t('register.contactUs.fields.phone.label')}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'register.contactUs.fields.phone.required'
                            ),
                          },
                        ]}
                      >
                        <CountryPhoneInput />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="organization"
                        label={t(
                          'register.contactUs.fields.organization.label'
                        )}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'register.contactUs.fields.organization.required'
                            ),
                          },
                        ]}
                      >
                        <Select
                          placeholder={t(
                            'register.contactUs.fields.organization.placeholder'
                          )}
                          onSelect={(value) => {
                            setOrganizationType(value);
                          }}
                        >
                          <Option value="company" className="select">
                            {t(
                              'register.contactUs.fields.organization.options.company'
                            )}
                          </Option>
                          <Option value="academy" className="select">
                            {t(
                              'register.contactUs.fields.organization.options.academy'
                            )}
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Col className="input-section">
                    <Col
                      className={`organization-section ${
                        organizationType != ''
                          ? ''
                          : 'organization-section-hidden'
                      }`}
                    >
                      {organizationType != '' && (
                        <>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                name="organizationName"
                                rules={[
                                  {
                                    required: true,
                                    message: t(
                                      'register.contactUs.fields.organizationName.required'
                                    ),
                                  },
                                ]}
                                label={
                                  organizationType == 'company'
                                    ? t(
                                        'register.contactUs.fields.organizationName.companyLabel'
                                      )
                                    : t(
                                        'register.contactUs.fields.organizationName.academyLabel'
                                      )
                                }
                              >
                                <Input
                                  className="input"
                                  placeholder={
                                    organizationType == 'company'
                                      ? t(
                                          'register.contactUs.fields.organizationName.companyPlaceholder'
                                        )
                                      : t(
                                          'register.contactUs.fields.organizationName.academyPlaceholder'
                                        )
                                  }
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              {' '}
                              <Form.Item
                                name="organizationEmail"
                                rules={[
                                  {
                                    required: true,
                                    message: t(
                                      'register.contactUs.fields.organizationEmail.required'
                                    ),
                                  },
                                  {
                                    type: 'email',
                                    message: t(
                                      'register.contactUs.fields.organizationEmail.invalid'
                                    ),
                                  },
                                ]}
                                label={
                                  organizationType == 'company'
                                    ? t(
                                        'register.contactUs.fields.organizationEmail.companyLabel'
                                      )
                                    : t(
                                        'register.contactUs.fields.organizationEmail.academyLabel'
                                      )
                                }
                              >
                                <Input
                                  className="input"
                                  placeholder={
                                    organizationType == 'company'
                                      ? t(
                                          'register.contactUs.fields.organizationEmail.companyPlaceholder'
                                        )
                                      : t(
                                          'register.contactUs.fields.organizationEmail.academyPlaceholder'
                                        )
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item
                            name="message"
                            label={t('register.contactUs.fields.message.label')}
                            rules={[
                              {
                                required: true,
                                message: t(
                                  'register.contactUs.fields.message.required'
                                ),
                              },
                            ]}
                          >
                            <Input.TextArea
                              rows={5}
                              placeholder={t(
                                'register.contactUs.fields.message.placeholder'
                              )}
                              className="input"
                            />
                          </Form.Item>
                        </>
                      )}
                    </Col>
                  </Col>
                </Form>
                <Flex className="btn-group">
                  <Button
                    className="btn-cancel"
                    type="default"
                    onClick={() => {
                      navigate(-1);
                      form.resetFields();
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="btn-send"
                    type="primary"
                    htmlType="submit"
                    disabled={!submittable}
                    onClick={handleSubmit}
                  >
                    {t('register.contactUs.buttons.send')}
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </StyledContactUs>
      )}
    </>
  );
};
export default ContactUs;
